import {
  CLIENT_FIELD_TYPES,
  CLIENT_FIELD_TYPE_LABEL,
  MAX_CLIENT_FIELDS,
  type ClientField,
  type ClientFieldType,
} from '@cyberscore/contracts';
import { ArrowDown, ArrowUp, Loader2, Pencil, Plus, SlidersHorizontal, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { Dialog, Drawer } from '../../ui/Overlay';
import { useToast } from '../../ui/Toasts';
import { useClientFieldChanges, useClientFields } from './api';

const keyOf = (label: string) =>
  label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^(\d)/, 'f_$1').slice(0, 40); // prettier-ignore

/**
 * The tenant's own client fields (task 66): what else it keeps about each client, shown in the client's
 * quick view. A field's type is fixed once any client has a value, and it can't be removed until none do.
 * `embedded`: inside Tenant 360's tab (task 102), under a smaller heading.
 */
export function ClientFieldsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const fields = useClientFields();
  const { order, remove } = useClientFieldChanges();
  const toast = useToast();
  const [editing, setEditing] = useState<ClientField | 'new' | null>(null);
  const [removing, setRemoving] = useState<ClientField | null>(null);
  const list = fields.data ?? [];
  const move = (i: number, by: -1 | 1) => {
    const keys = list.map((f) => f.key);
    [keys[i], keys[i + by]] = [keys[i + by]!, keys[i]!];
    order.mutate(keys);
  };

  return (
    <>
      <div className={`page-head row-between${embedded ? ' embedded' : ''}`}>
        <div>
          {embedded ? <h2>Client fields</h2> : <h1>Client fields</h1>}
          <p>What else you keep about each client, beside its policy and stage. Everyone who sees clients sees these; underwriters, brokers and admins fill them in.</p>
        </div>
        <button type="button" className="btn btn-primary" disabled={list.length >= MAX_CLIENT_FIELDS} onClick={() => setEditing('new')}><Plus size={15} /> Add field</button>
      </div>
      <section className="card">
        {fields.isPending ? (
          <div className="empty">Loading…</div>
        ) : fields.isError ? (
          <div className="empty"><strong>Could not load the fields</strong>{fields.error.message}</div>
        ) : !list.length ? (
          <div className="empty"><SlidersHorizontal size={28} /><strong>No client fields yet</strong>Add one for anything you track about clients, e.g. a market segment or claims in the last five years.</div>
        ) : (
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr><th>Field</th><th>Type</th><th className="wide">Options</th><th className="right">Clients with a value</th><th /></tr>
              </thead>
              <tbody>
                {list.map((f, i) => (
                  <tr key={f.key}>
                    <td><b>{f.label}</b><div className="muted small mono">{f.key}</div></td>
                    <td>{CLIENT_FIELD_TYPE_LABEL[f.type]}</td>
                    <td className="wide small">{f.options.join(', ') || <span className="muted">–</span>}</td>
                    <td className="right num">{f.used}</td>
                    <td className="right nowrap">
                      <button type="button" className="btn btn-ghost btn-icon btn-sm" aria-label={`Move ${f.label} up`} disabled={!i || order.isPending} onClick={() => move(i, -1)}><ArrowUp size={14} /></button>
                      <button type="button" className="btn btn-ghost btn-icon btn-sm" aria-label={`Move ${f.label} down`} disabled={i === list.length - 1 || order.isPending} onClick={() => move(i, 1)}><ArrowDown size={14} /></button>
                      <button type="button" className="btn btn-ghost btn-icon btn-sm" aria-label={`Edit ${f.label}`} onClick={() => setEditing(f)}><Pencil size={14} /></button>
                      <button type="button" className="btn btn-ghost btn-icon btn-sm" aria-label={`Remove ${f.label}`} title={f.used ? 'Clients have values for it: clear them first' : undefined} disabled={f.used > 0} onClick={() => setRemoving(f)}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {editing && <FieldDrawer field={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {removing && (
        <Dialog
          title={`Remove ${removing.label}?`}
          onClose={() => setRemoving(null)}
          actions={
            <>
              <button type="button" className="btn" onClick={() => setRemoving(null)}>Keep it</button>
              <button type="button" className="btn btn-danger" disabled={remove.isPending} onClick={async () => {
                try { await remove.mutateAsync(removing.key); toast(`Removed ${removing.label}.`); } catch (e) { toast(e instanceof Error ? e.message : 'Could not remove it', 'bad'); }
                setRemoving(null);
              }}>Remove field</button>
            </>
          }
        >
          <p>No client has a value for it. You can add it again later.</p>
        </Dialog>
      )}
    </>
  ); // prettier-ignore
}

function FieldDrawer({ field, onClose }: { field: ClientField | null; onClose: () => void }) {
  const { create, update } = useClientFieldChanges();
  const toast = useToast();
  const [label, setLabel] = useState(field?.label ?? '');
  const [key, setKey] = useState(field?.key ?? '');
  const [keyEdited, setKeyEdited] = useState(!!field);
  const [type, setType] = useState<ClientFieldType>(field?.type ?? 'text');
  const [options, setOptions] = useState((field?.options ?? []).join('\n'));
  const [help, setHelp] = useState(field?.help ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const saving = create.isPending || update.isPending;
  const typeFixed = !!field?.used;

  const save = async () => {
    const opts =
      type === 'choice'
        ? [
            ...new Set(
              options
                .split('\n')
                .map((o) => o.trim())
                .filter(Boolean),
            ),
          ]
        : [];
    const e: Record<string, string> = {};
    if (!label.trim()) e.label = 'Give the field a name';
    if (!field && !/^[a-z][a-z0-9_]{0,39}$/.test(key))
      e.key = 'Lower-case letters, digits and _, starting with a letter';
    if (type === 'choice' && !opts.length) e.options = 'List the options, one a line';
    setErrors(e);
    if (Object.keys(e).length) return;
    setBanner(null);
    try {
      if (field)
        await update.mutateAsync({
          key: field.key,
          label: label.trim(),
          type,
          options: opts,
          help: help.trim(),
        });
      else await create.mutateAsync({ key, label: label.trim(), type, options: opts, help: help.trim() });
      toast(`${label.trim()} saved.`);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(Object.fromEntries(err.issues.map((i) => [i.path, i.message])));
        setBanner(err.message);
      } else setBanner('Could not save. Try again.');
    }
  };

  return (
    <Drawer
      title={field ? `Edit ${field.label}` : 'Add a client field'}
      subtitle={field ? `${field.used} client${field.used === 1 ? ' has' : 's have'} a value` : 'It shows on every client’s quick view.'}
      onClose={onClose}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>{saving && <Loader2 size={15} className="spin" />}{field ? 'Save changes' : 'Add field'}</button>
        </>
      }
    >
      {banner && <div className="result bad" role="alert"><span>{banner}</span></div>}
      <div className="field">
        <label htmlFor="cf-label">Name</label>
        <input id="cf-label" className={`input${errors.label ? ' invalid' : ''}`} maxLength={100} value={label} onChange={(e) => { setLabel(e.target.value); if (!keyEdited) setKey(keyOf(e.target.value)); }} />
        {errors.label && <span className="error">{errors.label}</span>}
      </div>
      <div className="field">
        <label htmlFor="cf-key">Key</label>
        <input id="cf-key" className={`input mono${errors.key ? ' invalid' : ''}`} maxLength={40} value={key} disabled={!!field} onChange={(e) => { setKey(e.target.value); setKeyEdited(true); }} />
        <span className="hint">{field ? 'The key can’t change.' : 'How exports and the API name it. It can’t change later.'}</span>
        {errors.key && <span className="error">{errors.key}</span>}
      </div>
      <div className="field">
        <label htmlFor="cf-type">Type</label>
        <select id="cf-type" className="input" value={type} disabled={typeFixed} onChange={(e) => setType(e.target.value as ClientFieldType)}>
          {CLIENT_FIELD_TYPES.map((t) => <option key={t} value={t}>{CLIENT_FIELD_TYPE_LABEL[t]}</option>)}
        </select>
        {typeFixed && <span className="hint">Clients have values for it, so its type is fixed.</span>}
      </div>
      {type === 'choice' && (
        <div className="field">
          <label htmlFor="cf-options">Options, one a line</label>
          <textarea id="cf-options" className={`input${errors.options ? ' invalid' : ''}`} rows={5} value={options} onChange={(e) => setOptions(e.target.value)} />
          {field?.used ? <span className="hint">Options clients have chosen can’t be removed.</span> : null}
          {errors.options && <span className="error">{errors.options}</span>}
        </div>
      )}
      <div className="field">
        <label htmlFor="cf-help">Help text</label>
        <input id="cf-help" className="input" maxLength={300} value={help} onChange={(e) => setHelp(e.target.value)} placeholder="Optional: what to put here" />
      </div>
    </Drawer>
  ); // prettier-ignore
}
